import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, History, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { ApiError, errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { Breadcrumbs } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { bookQuery, useDeleteBook } from '@/features/library/api'
import { AvailabilityBadge } from '@/features/library/components/Badges'
import { BookCover } from '@/features/library/components/BookCover'
import { LoanTable } from '@/features/library/components/LoanTable'
import { formatIsbn } from '@/features/library/isbn'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDateTime } from '@/utils/format'

export function BookDetailPage() {
  const { t } = useTranslation('library')
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const { can, school } = useAuth()
  const { data, isPending, error, refetch } = useQuery(bookQuery(id))
  const remove = useDeleteBook()
  const [confirming, setConfirming] = useState(false)
  useDocumentTitle(data?.book.title ?? 'Book')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="book" homeHref="/admin/library" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const { book, loans, timesBorrowed } = data
  const currency = school?.currency ?? 'USD'
  const open = loans.filter((l) => l.state !== 'returned')
  const history = loans.filter((l) => l.state === 'returned')
  const pct = book.totalCopies ? (book.onLoan / book.totalCopies) * 100 : 0

  const onDelete = async () => {
    try {
      await remove.mutateAsync(book.id)
      toast.success(t('Book deleted'), book.title)
      void navigate('/admin/library')
    } catch (e) {
      setConfirming(false)
      toast.error(t('Could not delete'), errorMessage(e))
    }
  }

  return (
    <>
      <Breadcrumbs
        homeHref="/admin"
        items={[{ label: t('Library'), to: '/admin/library' }, { label: book.title }]}
      />
      <div className="mt-3 grid gap-6 lg:grid-cols-[auto_1fr]">
        <div className="flex justify-center lg:block">
          <BookCover title={book.title} authors={book.authors} src={book.coverUrl} size="lg" />
        </div>
        <div className="min-w-0 space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-balance">{book.title}</h1>
                <AvailabilityBadge book={book} size="md" />
              </div>
              {book.subtitle ? <p className="mt-1 text-ink-soft">{book.subtitle}</p> : null}
              <p className="mt-1 text-sm text-muted">{book.authors}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {book.category ? <Badge tone="brand">{book.category.name}</Badge> : null}
                {book.language ? <Badge>{book.language}</Badge> : null}
                {book.shelfLocation ? (
                  <Badge>{t('Shelf {{location}}', { location: book.shelfLocation })}</Badge>
                ) : null}
              </div>
            </div>
            <Can permission="library.manage">
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="secondary" leftIcon={<Pencil />}>
                  <Link to={`/admin/library/books/${book.id}/edit`}>{t('Edit')}</Link>
                </Button>
                {book.active && book.availableCopies > 0 ? (
                  <Button asChild leftIcon={<ArrowLeftRight />}>
                    <Link to="/admin/library/circulation">{t('Issue a copy')}</Link>
                  </Button>
                ) : null}
                {timesBorrowed === 0 ? (
                  <Button variant="danger-soft" leftIcon={<Trash2 />} onClick={() => setConfirming(true)}>
                    {t('Delete')}
                  </Button>
                ) : null}
              </div>
            </Can>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader
                title={t('Copies')}
                description={t('Borrowed {{count}} times in total', { count: timesBorrowed })}
              />
              <CardBody className="space-y-4">
                <dl className="grid grid-cols-3 gap-4 text-center">
                  {[
                    [t('Owned'), book.totalCopies],
                    [t('Available'), book.availableCopies],
                    [t('On loan'), book.onLoan],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-control bg-surface-muted p-3">
                      <dt className="text-xs text-muted">{label}</dt>
                      <dd className="mt-1 text-2xl font-semibold text-ink tabular">{value}</dd>
                    </div>
                  ))}
                </dl>
                <div
                  className="h-2 overflow-hidden rounded-full bg-surface-sunken"
                  role="img"
                  aria-label={t('{{onLoan}} of {{total}} copies on loan', {
                    onLoan: book.onLoan,
                    total: book.totalCopies,
                    count: book.onLoan,
                  })}
                >
                  <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardHeader title={t('Details')} />
              <CardBody>
                <DescriptionList
                  items={[
                    {
                      label: t('ISBN'),
                      value: <span className="font-mono text-xs">{formatIsbn(book.isbn)}</span>,
                    },
                    { label: t('Publisher'), value: book.publisher },
                    { label: t('Published'), value: book.publishedYear },
                    { label: t('Updated'), value: formatDateTime(book.updatedAt) },
                    ...(book.description
                      ? [{ label: t('Description'), value: book.description, span: 2 as const }]
                      : []),
                  ]}
                />
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader
              title={t('Currently on loan')}
              description={open.length ? t('{{count}} copies out', { count: open.length }) : undefined}
              icon={<ArrowLeftRight />}
            />
            <LoanTable
              caption={t('Open loans of this book')}
              loans={open}
              currency={currency}
              showBook={false}
              actions={can('library.manage')}
              empty={<EmptyState title={t('Every copy is on the shelf')} compact />}
            />
          </Card>
          <Card>
            <CardHeader title={t('Loan history')} description={t('Most recent returns')} icon={<History />} />
            <LoanTable
              caption={t('Loan history of this book')}
              loans={history}
              currency={currency}
              showBook={false}
              actions={can('library.manage')}
              empty={<EmptyState title={t('Not borrowed yet')} compact />}
            />
          </Card>
        </div>
      </div>
      {confirming ? (
        <ConfirmDialog
          open
          onOpenChange={setConfirming}
          title={t('Delete this book?')}
          description={t('“{{title}}” has never been borrowed and will be removed from the catalogue.', {
            title: book.title,
          })}
          confirmLabel={t('Delete book')}
          loading={remove.isPending}
          onConfirm={() => void onDelete()}
        />
      ) : null}
    </>
  )
}
